import { useEffect, useMemo, useState } from "react";
import { FiClipboard, FiSave, FiTrash, FiX } from "react-icons/fi";
import { Input, Modal } from "@iq/models";
import {
	addProjectMember,
	getProjectById,
	getTeamMembers,
	deleteProject,
	removeProjectMember,
	updateProject,
} from "../../Services/pm_service";
import "../../../CRM/components/modals/Modal.css";
import "../../Styles/ViewProjectModal.css";

const PROJECT_STATUSES = ["active", "on_hold", "completed", "archived"];

function toStatusLabel(status) {
	return String(status || "")
		.split("_")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

function formatTimestamp(value) {
	if (!value) {
		return "-";
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return date.toLocaleString();
}

function getTaskAssignedUserId(task) {
	return Number(task?.assigned_user_id || task?.assignedUserId || task?.assigned_user?.id || 0);
}

function ViewProjectModal({ isOpen, onClose, project, tasks = [], onSaved, onDeleted }) {
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		status: "active",
	});
	const [projectMembers, setProjectMembers] = useState([]);
	const [originalMemberIds, setOriginalMemberIds] = useState([]);
	const [ownerUserId, setOwnerUserId] = useState(null);
	const [selectedMemberLabel, setSelectedMemberLabel] = useState("");
	const [selectedStatusLabel, setSelectedStatusLabel] = useState("Active");
	const [teamMembers, setTeamMembers] = useState([]);
	const [contextLoading, setContextLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [error, setError] = useState("");

	const projectId = Number(project?.id || 0);

	const availableMembers = useMemo(
		() =>
			teamMembers.filter(
				(member) => !projectMembers.some((selected) => Number(selected.id) === Number(member.id))
			),
		[teamMembers, projectMembers]
	);

	const canSave = useMemo(() => {
		return Boolean(formData.title.trim()) && !saving && !deleting;
	}, [deleting, formData.title, saving]);

	const totalTasks = tasks.length;
	const completedTasks = useMemo(
		() => tasks.filter((task) => String(task?.status || "").toLowerCase() === "done").length,
		[tasks]
	);
	const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

	const completedByMember = useMemo(() => {
		const memberLookup = new Map(projectMembers.map((member) => [Number(member.id), member]));
		const counts = new Map();

		tasks.forEach((task) => {
			if (String(task?.status || "").toLowerCase() !== "done") {
				return;
			}

			const assignedUserId = getTaskAssignedUserId(task);
			if (assignedUserId <= 0) {
				counts.set("unassigned", (counts.get("unassigned") || 0) + 1);
				return;
			}

			const member = memberLookup.get(assignedUserId);
			const key = member
				? `member-${assignedUserId}`
				: `external-${assignedUserId}`;
			counts.set(key, (counts.get(key) || 0) + 1);
		});

		return Array.from(counts.entries())
			.map(([key, count]) => {
				if (key === "unassigned") {
					return { key, label: "Unassigned", count };
				}

				const [prefix, rawId] = String(key).split("-");
				const userId = Number(rawId);
				const member = memberLookup.get(userId);
				if (member) {
					return {
						key,
						label: member.name || member.email || `User #${member.id}`,
						count,
					};
				}

				return {
					key,
					label:
						prefix === "external" ? `Former member #${userId}` : `User #${userId}`,
					count,
				};
			})
			.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
	}, [projectMembers, tasks]);

	const actionButtons = [
		{
			key: "delete-project",
			text: deleting ? "Deleting..." : "Delete Project",
			icon: <FiTrash />,
			type: "button",
			onClick: () => handleDelete(),
			disabled: contextLoading || saving || deleting,
			color: "danger",
		},
		{
			key: "save-project",
			text: saving ? "Saving..." : "Save Changes",
			icon: <FiSave />,
			type: "button",
			onClick: () => handleSave(),
			disabled: !canSave || contextLoading || deleting,
			color: "accent",
		},
	];

	useEffect(() => {
		if (!isOpen || !projectId) {
			return;
		}

		let cancelled = false;

		async function loadContext() {
			setContextLoading(true);
			setError("");
			try {
				const [members, projectDetails] = await Promise.all([
					getTeamMembers(),
					getProjectById(projectId),
				]);

				if (cancelled) {
					return;
				}

				const detailedMembers = Array.isArray(projectDetails?.members) ? projectDetails.members : [];
				const normalizedMembers = detailedMembers
					.map((entry) => ({
						id: Number(entry?.user_id || entry?.user?.id || 0),
						name: entry?.user?.name || "",
						email: entry?.user?.email || "",
						role: entry?.role || "member",
					}))
					.filter((member) => member.id > 0);

				setTeamMembers(Array.isArray(members) ? members : []);
				setProjectMembers(normalizedMembers);
				setOriginalMemberIds(normalizedMembers.map((member) => Number(member.id)));
				setOwnerUserId(Number(projectDetails?.owner_user_id || projectDetails?.owner_user?.id || 0));
				setFormData({
					title: projectDetails?.title || project?.title || "",
					description: projectDetails?.description || "",
					status: projectDetails?.status || project?.status || "active",
				});
				setSelectedStatusLabel(
					toStatusLabel(projectDetails?.status || project?.status || "active")
				);
				setSelectedMemberLabel("");
			} catch (contextError) {
				if (!cancelled) {
					setError(contextError?.message || "Failed to load project details");
				}
			} finally {
				if (!cancelled) {
					setContextLoading(false);
				}
			}
		}

		loadContext();

		return () => {
			cancelled = true;
		};
	}, [isOpen, project?.id, project?.status, project?.title, projectId]);

	if (!isOpen || !projectId) {
		return null;
	}

	function handleFieldChange(event) {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		setError("");
	}

	function handleAddMember(member) {
		if (projectMembers.some((existing) => Number(existing.id) === Number(member.id))) {
			setError(`${member.name || member.email} is already selected`);
			return;
		}

		setProjectMembers((prev) => [
			...prev,
			{
				id: Number(member.id),
				name: member.name || "",
				email: member.email || "",
				role: "member",
			},
		]);
		setSelectedMemberLabel("");
		setError("");
	}

	function handleRemoveMember(memberId) {
		if (Number(ownerUserId) === Number(memberId)) {
			setError("Project owner cannot be removed from members");
			return;
		}

		setProjectMembers((prev) => prev.filter((member) => Number(member.id) !== Number(memberId)));
	}

	async function handleSave() {
		const title = formData.title.trim();
		if (!title) {
			setError("Project title is required");
			return;
		}

		try {
			setSaving(true);
			setError("");

			const updatedProject = await updateProject(projectId, {
				title,
				description: formData.description.trim(),
				status: formData.status,
			});

			const selectedIds = projectMembers.map((member) => Number(member.id));
			const toAdd = selectedIds.filter((id) => !originalMemberIds.includes(id));
			const toRemove = originalMemberIds.filter(
				(id) => !selectedIds.includes(id) && Number(id) !== Number(ownerUserId)
			);

			const memberOps = [
				...toAdd.map((userId) =>
					addProjectMember(projectId, {
						userId,
						role: "member",
					})
				),
				...toRemove.map((userId) => removeProjectMember(projectId, userId)),
			];

			if (memberOps.length > 0) {
				const results = await Promise.allSettled(memberOps);
				const failedCount = results.filter((result) => result.status === "rejected").length;
				if (failedCount > 0) {
					window.alert(
						`Project saved, but ${failedCount} member update(s) could not be applied.`
					);
				}
			}

			onSaved?.(updatedProject);
			onClose?.();
		} catch (saveError) {
			setError(saveError?.message || "Failed to save project changes");
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete() {
		if (!projectId || saving || deleting) {
			return;
		}

		const projectTitle = (formData.title || project?.title || `Project #${projectId}`).trim();
		const confirmed = window.confirm(
			`Delete "${projectTitle}"? This will permanently delete the project, all tasks, comments, and members.`
		);
		if (!confirmed) {
			return;
		}

		try {
			setDeleting(true);
			setError("");
			await deleteProject(projectId);
			onDeleted?.(projectId);
			onClose?.();
		} catch (deleteError) {
			setError(deleteError?.message || "Failed to delete project");
		} finally {
			setDeleting(false);
		}
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={formData.title || "View Project"}
			icon={<FiClipboard />}
			actionButtons={actionButtons}
			errorMessage={error}
			tall
		>
			<div className="pm-project-view-layout">
				<div className="pm-project-view-left">
					<div className="modal-form-field wide-form-field">
						<label htmlFor="pm-view-project-title">Project Name</label>
						<Input
							id="pm-view-project-title"
							type="text"
							name="title"
							value={formData.title}
							onChange={handleFieldChange}
							disabled={saving || contextLoading}
							colorScheme="light"
						/>
					</div>

					<div className="modal-form-field wide-form-field">
						<label className="attendees-label">Project Members</label>
						<div className="pm-project-members-container">
							<div className="attendees-control-row">
							<Input
								type="dropdown"
								id="pm-view-project-members"
								name="pm-view-project-members"
								className="attendees-dropdown"
								placeholder={
									availableMembers.length === 0
										? "All team members selected"
										: "Select members to add..."
								}
								value={selectedMemberLabel}
								allowCustom={false}
								colorScheme="light"
								ariaLabel="Project members"
								renderInPortal
								disabled={saving || deleting || contextLoading || availableMembers.length === 0}
							>
								{({ closeMenu }) => (
									<>
										{availableMembers.map((member) => (
											<button
												key={member.id}
												type="button"
												className="dropdown-menu-item"
												onClick={() => {
													handleAddMember(member);
													setSelectedMemberLabel("");
													closeMenu();
												}}
											>
												{member.name} ({member.email})
											</button>
										))}
										{availableMembers.length === 0 ? (
											<div className="dropdown-menu-empty">All team members selected</div>
										) : null}
									</>
								)}
							</Input>
							</div>
							{projectMembers.length > 0 ? (
								<div className="pm-project-member-rows" role="list" aria-label="Project members list">
									{projectMembers.map((member) => {
										const isOwner = Number(member.id) === Number(ownerUserId);
										return (
											<div key={member.id} className="pm-project-member-row" role="listitem">
												<div className="pm-project-member-row-main">
													<span className="pm-project-member-row-name">
														{member.name || member.email || `User #${member.id}`}
													</span>
													<span className="pm-project-member-row-meta">
														{isOwner ? "Owner" : "Member"}
													</span>
												</div>
												<button
													type="button"
													className="pm-project-member-row-remove"
													onClick={() => handleRemoveMember(member.id)}
													disabled={saving || deleting || contextLoading || isOwner}
													aria-label={`Remove ${member.name || member.email || member.id}`}
													title={isOwner ? "Owner cannot be removed" : "Remove member"}
												>
													<FiX />
												</button>
											</div>
										);
									})}
								</div>
							) : (
								<div className="pm-project-member-empty">No members selected</div>
							)}
						</div>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
						<label>Status</label>
						<Input
							type="dropdown"
							id="pm-view-project-status"
							name="pm-view-project-status"
							placeholder="Status"
							value={selectedStatusLabel}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Project status"
							renderInPortal
							disabled={saving || deleting || contextLoading}
							options={PROJECT_STATUSES.map((status) => ({ value: status, label: toStatusLabel(status) }))}
							onOptionSelect={(option) => {
								setFormData((prev) => ({ ...prev, status: option.value }));
								setSelectedStatusLabel(option.label);
							}}
						/>
					</div>

					<div className="modal-form-field wide-form-field view-project-description-field">
						<label htmlFor="pm-view-project-description">Description</label>
						<Input
							id="pm-view-project-description"
							type="textarea"
							name="description"
							value={formData.description}
							onChange={handleFieldChange}
							rows={5}
							disabled={saving || contextLoading}
							colorScheme="light"
						/>
					</div>
				</div>

				<div className="pm-project-view-divider" />

				<div className="pm-project-view-right">
					<h3 className="pm-project-metadata-header">Project Snapshot</h3>
					<div className="pm-project-metadata-grid">
						<div className="pm-project-metadata-card">
							<span>Total Tasks</span>
							<strong>{totalTasks}</strong>
						</div>
						<div className="pm-project-metadata-card">
							<span>Tasks Completed</span>
							<strong>{completedTasks}</strong>
						</div>
						<div className="pm-project-metadata-card">
							<span>Completion Rate</span>
							<strong>{completionRate}%</strong>
						</div>
						<div className="pm-project-metadata-card">
							<span>Team Members</span>
							<strong>{projectMembers.length}</strong>
						</div>
					</div>

					<div className="pm-project-metadata-section">
						<h4>Tasks Completed by Member</h4>
						{completedByMember.length === 0 ? (
							<div className="pm-project-metadata-empty">No completed tasks yet.</div>
						) : (
							<div className="pm-project-metadata-list">
								{completedByMember.map((entry) => (
									<div key={entry.key} className="pm-project-metadata-row">
										<span>{entry.label}</span>
										<strong>{entry.count}</strong>
									</div>
								))}
							</div>
						)}
					</div>

					<div className="pm-project-metadata-section">
						<h4>Project Info</h4>
						<div className="pm-project-metadata-list">
							<div className="pm-project-metadata-row">
								<span>Project ID</span>
								<strong>{projectId}</strong>
							</div>
							<div className="pm-project-metadata-row">
								<span>Owner</span>
								<strong>
									{project?.owner_user?.name || project?.owner_user?.email || `User #${ownerUserId || "-"}`}
								</strong>
							</div>
							<div className="pm-project-metadata-row">
								<span>Created</span>
								<strong>{formatTimestamp(project?.created_at || project?.createdAt)}</strong>
							</div>
							<div className="pm-project-metadata-row">
								<span>Last Updated</span>
								<strong>{formatTimestamp(project?.updated_at || project?.updatedAt)}</strong>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
}

export default ViewProjectModal;
